/**
 * DealInvestFlow — Reusable multi-step investment modal for ANY deal.
 *
 * Steps:
 *   1. method   — Choose payment rail (ACH / Wire / TAO / Crypto)
 *   2. bank     — Link or select bank account (ACH only)
 *   3. amount   — Enter investment amount
 *   4. review   — Confirm details + accept agreement
 *   5. processing — Spinner while API call runs
 *   6. receipt  — Confirmation with txn ID + links to portal
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, ArrowLeft, Building2, Banknote, Wallet, CheckCircle2, AlertCircle,
  Copy, Loader2, Plus, Shield, ArrowRight, ExternalLink, Clock, Receipt, Phone,
  FileText, Download, Landmark,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { dealInvestApi, plaidApi, DealInvestInfo, DealInvestResult } from '../../services/api';
import PlaidLinkButton from '../Plaid/PlaidLinkButton';
import { TENANT } from '../../config';
import config from '../../config';
import { useUser } from '../../contexts/UserContext';
import { DocEsignStep } from './HiveFundInvest/steps/DocEsignStep';
import { SignaturePad } from './SignaturePad';
import { SubscriptionWizard } from './SubscriptionWizard';
import type { SubscriptionData } from '../../services/api';

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

type FlowStep = 'method' | 'bank' | 'amount' | 'review' | 'subscription' | 'docEsign' | 'signature' | 'processing' | 'receipt' | 'tao';

interface PlaidAccount {
  id: string;
  account_id?: string;
  account_name: string;
  name?: string;
  last4?: string;
  mask?: string;
  account_type: string;
  type?: string;
  subtype: string;
  institution_name?: string;
}

export interface DealInvestFlowProps {
  /** Deal slug used to fetch info and submit investment */
  dealSlug: string;
  /** Pre-loaded deal info (optional, avoids a fetch) */
  dealInfo?: DealInvestInfo;
  /** Called when the modal closes */
  onClose: () => void;
  /** Fund wallet address for crypto/TAO (falls back to dealInfo.fundWalletAddress) */
  fundWalletAddress?: string;
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

export function DealInvestFlow({ dealSlug, dealInfo: prefetchedInfo, onClose, fundWalletAddress: propWallet }: DealInvestFlowProps) {
  const { canInvest, kycStatus } = useUser();
  // --- state ---
  const [step, setStep] = useState<FlowStep>('method');
  const [info, setInfo] = useState<DealInvestInfo | null>(prefetchedInfo ?? null);
  const [loading, setLoading] = useState(!prefetchedInfo);

  const [method, setMethod] = useState<'ach' | 'wire' | 'tao' | 'crypto' | 'investment_account' | null>(null);
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [taoAmount, setTaoAmount] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRiskDisclosure, setAcceptRiskDisclosure] = useState(false);
  const [acceptAchAuth, setAcceptAchAuth] = useState(false);
  const [investorPhone, setInvestorPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DealInvestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [receiptDocs, setReceiptDocs] = useState<Array<{ id: string; title: string; filename: string; document_type: string; download_url: string }>>([]);
  const [esignDocs, setEsignDocs] = useState<Array<{ id: string; title: string; filename: string; download_url: string }>>([]);
  const [signedDocIds, setSignedDocIds] = useState<string[]>([]);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);

  const walletAddress = propWallet ?? info?.fundWalletAddress ?? null;
  const minInvest = info?.minInvestment ?? 0;
  const amountNum = parseFloat(amount) || 0;

  // --- fetch deal info if not provided ---
  useEffect(() => {
    if (prefetchedInfo) return;
    (async () => {
      setLoading(true);
      const res = await dealInvestApi.getInfo(dealSlug);
      if (res.success && res.data) setInfo(res.data);
      else setError(res.message ?? 'Failed to load deal info');
      setLoading(false);
    })();
  }, [dealSlug, prefetchedInfo]);

  // --- fetch bank accounts when entering bank step ---
  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await plaidApi.getAccounts();
      if ((res as any).data?.success) {
        setAccounts(((res as any).data.data?.accounts as PlaidAccount[]) ?? []);
      }
    } catch {
      /* ignore */
    }
    setAccountsLoading(false);
  }, []);

  useEffect(() => {
    if (step === 'bank') fetchAccounts();
  }, [step, fetchAccounts]);

  // --- load esign-required documents once deal info is available ---
  useEffect(() => {
    if (!info?.id) return;
    (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/offerings/${info.id}/documents`);
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const required = data.data.filter((d: any) => d.requires_esign);
          setEsignDocs(required.map((d: any) => ({
            id: d.id,
            title: d.title || d.filename,
            filename: d.filename,
            download_url: `${config.apiBaseUrl}/documents/deal/${d.id}`,
          })));
        }
      } catch { /* silent */ }
    })();
  }, [info?.id]);

  // --- fetch deal documents when receipt shows ---
  useEffect(() => {
    if (step !== 'receipt' || !info?.id) return;
    (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/offerings/${info.id}/documents`);
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setReceiptDocs(data.data.map((d: any) => ({
            id: d.id,
            title: d.title || d.filename,
            filename: d.filename,
            document_type: d.document_type || d.visible_to || 'document',
            download_url: `${config.apiBaseUrl}/documents/deal/${d.id}`,
          })));
        }
      } catch { /* silent */ }
    })();
  }, [step, info?.id]);

  // --- navigation helpers ---
  const goBack = () => {
    setError(null);
    switch (step) {
      case 'bank': setStep('method'); break;
      case 'amount': setStep(method === 'ach' ? 'bank' : 'method'); break;
      case 'review': setStep('amount'); break;
      case 'subscription': setStep('review'); break;
      case 'docEsign': setStep('subscription'); break;
      case 'signature': setStep(esignDocs.length > 0 ? 'docEsign' : 'subscription'); break;
      case 'tao': setStep('method'); break;
      default: onClose();
    }
  };

  // --- review continue: route through subscription / esign / signature gates ---
  const handleReviewContinue = () => {
    setStep('subscription');
  };

  // --- subscription wizard complete: continue to esign or signature ---
  const handleSubscriptionComplete = (data: SubscriptionData) => {
    setSubscriptionData(data);
    if (esignDocs.length > 0) { setStep('docEsign'); return; }
    setStep('signature');
  };

  // --- submit (called from signature step) ---
  const handleSubmit = async () => {
    if (!info || !method) return;
    setSubmitting(true);
    setError(null);
    setStep('processing');

    const payload: Parameters<typeof dealInvestApi.invest>[1] = {
      amount: amountNum,
      method,
      agreementAccepted: true,
      phone: investorPhone || undefined,
      legal_acceptances: {
        terms: acceptTerms,
        risk_disclosure: acceptRiskDisclosure,
        ...(method === 'ach' ? { ach_authorization: acceptAchAuth } : {}),
      },
      signedDocIds: signedDocIds.length > 0 ? signedDocIds : undefined,
      signatureImage: signatureImage || undefined,
      subscriptionData: subscriptionData || undefined,
    };
    if (method === 'ach' && selectedAccountId) payload.bankAccountId = selectedAccountId;
    if (method === 'tao') {
      payload.amount = parseFloat(taoAmount) || 0;
      payload.currency = 'TAO';
      if (walletAddress) payload.walletAddress = walletAddress;
    }

    const res = await dealInvestApi.invest(dealSlug, payload);
    setSubmitting(false);

    if (res.success && res.data) {
      setResult(res.data);
      setStep('receipt');
    } else {
      setError(res.message ?? 'Investment failed');
      setStep('review');
    }
  };

  // --- TAO submit (same but for tao flow) ---
  const handleTaoSubmit = async () => {
    if (!info) return;
    setSubmitting(true);
    setError(null);
    setStep('processing');

    const res = await dealInvestApi.invest(dealSlug, {
      amount: parseFloat(taoAmount) || 0,
      method: 'tao',
      currency: 'TAO',
      walletAddress: walletAddress ?? undefined,
      agreementAccepted: true,
    });
    setSubmitting(false);

    if (res.success && res.data) {
      setResult(res.data);
      setStep('receipt');
    } else {
      setError(res.message ?? 'Investment failed');
      setStep('tao');
    }
  };

  const copyAddress = () => {
    if (!walletAddress) return;
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // --- accepted methods from deal ---
  const acceptedMethods = info?.acceptedMethods ?? ['ach', 'wire', 'crypto', 'investment_account'];

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (loading) {
    return (
      <Overlay onClose={onClose}>
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      {/* Header with back + step title */}
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div className="flex items-center gap-3">
          {step !== 'method' && step !== 'processing' && step !== 'receipt' && (
            <button onClick={goBack} className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-lg font-semibold text-foreground">{stepTitle(step, method)}</h2>
        </div>
        {step !== 'processing' && (
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        )}
      </div>

      {/* Progress dots */}
      {step !== 'processing' && step !== 'receipt' && (
        <StepIndicator current={step} method={method} hasEsign={esignDocs.length > 0} />
      )}

      {/* Error banner */}
      {error && step !== 'processing' && (
        <div className="mx-5 mt-4 flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ======= KYC GATE ======= */}
      {step === 'method' && !canInvest().canInvest && (
        <div className="p-5 space-y-4 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
            <Shield className="w-6 h-6 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold">Identity Verification Required</h3>
          <p className="text-sm text-muted-foreground">
            To comply with federal securities regulations, you must complete identity verification (KYC) before investing.
          </p>
          <p className="text-sm text-muted-foreground">
            Current status: <span className="font-medium text-amber-500 capitalize">{kycStatus || 'not started'}</span>
          </p>
          <Button className="w-full" onClick={onClose}>Close</Button>
        </div>
      )}

      {/* ======= STEP: method ======= */}
      {step === 'method' && canInvest().canInvest && (
        <div className="p-5 space-y-3">
          <p className="text-sm text-muted-foreground mb-4">
            Choose how you'd like to invest in <span className="font-medium text-foreground">{info?.title}</span>.
            {minInvest > 0 && <> Minimum: {formatCurrency(minInvest)}.</>}
          </p>

          {acceptedMethods.includes('ach') && (
            <MethodCard
              icon={<Building2 className="w-6 h-6 text-primary" />}
              title="Bank Transfer (ACH)"
              desc="Link your bank → enter amount → confirm. 3-5 business days."
              recommended
              onClick={() => { setMethod('ach'); setStep('bank'); }}
            />
          )}

          {acceptedMethods.includes('wire') && (
            <MethodCard
              icon={<Banknote className="w-6 h-6 text-primary" />}
              title="Wire Transfer"
              desc="For larger investments. Contact our team for wiring instructions."
              onClick={() => { setMethod('wire'); setStep('amount'); }}
            />
          )}

          {acceptedMethods.includes('tao') && (
            <MethodCard
              icon={<Wallet className="w-6 h-6 text-primary" />}
              title="Invest with TAO"
              desc="Transfer TAO directly from your Bittensor wallet to the fund."
              onClick={() => { setMethod('tao'); setStep('tao'); }}
            />
          )}

          {acceptedMethods.includes('crypto') && !acceptedMethods.includes('tao') && (
            <MethodCard
              icon={<Wallet className="w-6 h-6 text-primary" />}
              title="Crypto"
              desc="Transfer crypto directly. We'll confirm on-chain."
              onClick={() => { setMethod('crypto'); setStep('amount'); }}
            />
          )}

          {acceptedMethods.includes('investment_account') && (
            <MethodCard
              icon={<Landmark className="w-6 h-6 text-primary" />}
              title="Investment Account"
              desc="Fund from a brokerage, IRA, 401(k), or self-directed custodial account."
              onClick={() => { setMethod('investment_account'); setStep('amount'); }}
            />
          )}
        </div>
      )}

      {/* ======= STEP: bank (link + select) ======= */}
      {step === 'bank' && (
        <div className="p-5 space-y-5">
          <p className="text-sm text-muted-foreground">
            Select a linked bank account or connect a new one via Plaid.
          </p>

          {accountsLoading ? (
            <div className="space-y-3">
              <div className="h-16 rounded-xl bg-secondary animate-pulse" />
              <div className="h-16 rounded-xl bg-secondary animate-pulse" />
            </div>
          ) : (
            <>
              {accounts.length > 0 && (
                <div className="space-y-2" role="radiogroup" aria-label="Bank accounts">
                  {accounts.map(acct => {
                    const acctId = acct.id || acct.account_id || '';
                    const selected = selectedAccountId === acctId;
                    return (
                      <button
                        key={acctId}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setSelectedAccountId(acctId)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                          selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium capitalize">{acct.subtype || acct.account_type || acct.type} ···{acct.last4 || acct.mask}</p>
                          <p className="text-xs text-muted-foreground truncate">{acct.account_name || acct.name}</p>
                        </div>
                        {selected && <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}

              <PlaidLinkButton
                onSuccess={(newAccounts: any[]) => {
                  setAccounts(prev => [...prev, ...newAccounts]);
                  if (newAccounts.length) setSelectedAccountId(newAccounts[0].id || newAccounts[0].account_id);
                }}
                variant="outline"
                className="w-full h-12 justify-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                {accounts.length === 0 ? 'Link a bank account' : 'Link another account'}
              </PlaidLinkButton>
            </>
          )}

          <Button
            onClick={() => setStep('amount')}
            disabled={!selectedAccountId}
            className="w-full h-12"
          >
            Continue
          </Button>
        </div>
      )}

      {/* ======= STEP: amount ======= */}
      {step === 'amount' && (
        <div className="p-5 space-y-5">
          <p className="text-sm text-muted-foreground">
            {method === 'ach' && 'Your bank account will be debited via ACH. 3-5 business days to settle.'}
            {method === 'wire' && 'Enter the amount you plan to wire. Instructions will be provided after confirmation.'}
            {method === 'crypto' && 'Enter the USD-equivalent value of your investment.'}
          </p>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Investment Amount (USD)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">$</span>
              <input
                type="number"
                min={minInvest}
                step="100"
                placeholder={minInvest > 0 ? `Min ${formatCurrency(minInvest)}` : 'Enter amount'}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full pl-8 pr-3 py-3 rounded-lg border border-border bg-background text-foreground text-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            {minInvest > 0 && amountNum > 0 && amountNum < minInvest && (
              <p className="text-xs text-destructive mt-1">Minimum investment is {formatCurrency(minInvest)}</p>
            )}
          </div>

          {/* Quick amounts */}
          {minInvest > 0 && (
            <div className="flex flex-wrap gap-2">
              {[minInvest, minInvest * 2, minInvest * 5, minInvest * 10].filter(v => {
                const tgt = info?.raiseTarget;
                return !tgt || v <= tgt;
              }).map(v => (
                <button
                  key={v}
                  onClick={() => setAmount(String(v))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                    amountNum === v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40 text-muted-foreground'
                  }`}
                >
                  {formatCurrency(v)}
                </button>
              ))}
            </div>
          )}

          <Button
            onClick={() => setStep('review')}
            disabled={amountNum <= 0 || (minInvest > 0 && amountNum < minInvest)}
            className="w-full h-12"
          >
            Review Investment
          </Button>
        </div>
      )}

      {/* ======= STEP: review ======= */}
      {step === 'review' && (
        <div className="p-5 space-y-5">
          <Card className="p-4 border-border space-y-3">
            <Row label="Deal" value={info?.title ?? dealSlug} />
            <Row label="Amount" value={formatCurrency(amountNum)} bold />
            <Row label="Method" value={methodLabel(method)} />
            {method === 'ach' && selectedAccountId && (
              <Row label="Bank Account" value={`···${accounts.find(a => (a.id || a.account_id) === selectedAccountId)?.last4 ?? accounts.find(a => (a.id || a.account_id) === selectedAccountId)?.mask ?? '****'}`} />
            )}
            <Row label="Settlement" value={method === 'ach' ? '3-5 business days' : method === 'wire' ? '1-2 business days' : 'Varies'} />
          </Card>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Phone Number <span className="text-red-500">*</span>
              </span>
            </label>
            <input
              type="tel"
              value={investorPhone}
              onChange={e => setInvestorPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <p className="text-xs text-muted-foreground mt-1">Required for transaction verification</p>
          </div>

          {/* Required Agreements */}
          <div className="border border-border rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-muted-foreground" /> Required Agreements
            </p>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary" />
              <span className="text-xs text-muted-foreground group-hover:text-foreground leading-relaxed">
                I have read and agree to the <strong className="text-foreground">Terms of Service</strong> and <strong className="text-foreground">Privacy Policy</strong>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" checked={acceptRiskDisclosure} onChange={e => setAcceptRiskDisclosure(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary" />
              <span className="text-xs text-muted-foreground group-hover:text-foreground leading-relaxed">
                I acknowledge the <strong className="text-foreground">Risk Disclosures</strong> associated with this transaction.
              </span>
            </label>
            {method === 'ach' && (
              <>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={acceptAchAuth} onChange={e => setAcceptAchAuth(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-border accent-primary" />
                  <span className="text-xs text-muted-foreground group-hover:text-foreground leading-relaxed">
                    I authorize <strong className="text-foreground">{TENANT.branding.name}</strong> (via Increase.com) to initiate a one-time <strong className="text-foreground">ACH debit</strong> of <strong className="text-foreground">{formatCurrency(amountNum)}</strong> from my linked bank account. This authorization remains in effect for this transaction only. I may revoke this authorization by contacting support before the transfer is initiated. I agree that this transaction is subject to <strong className="text-foreground">NACHA Operating Rules</strong> and my financial institution's policies.
                  </span>
                </label>
                {/* ACH Same-Day Cutoff Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                  <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>Same-Day ACH Notice:</strong> ACH transactions initiated before <strong>4:45 PM ET</strong> on a business day are eligible for same-day processing. Transactions submitted after this cutoff or on weekends/holidays will be processed on the next business day.
                  </p>
                </div>
              </>
            )}
          </div>

          <Button
            onClick={handleReviewContinue}
            disabled={!acceptTerms || !acceptRiskDisclosure || (method === 'ach' && !acceptAchAuth) || investorPhone.replace(/\D/g, '').length < 10}
            className="w-full h-12 text-base font-semibold"
          >
            {esignDocs.length > 0 ? 'Continue to Sign Documents' : 'Continue to Sign & Submit'}
          </Button>
        </div>
      )}

      {/* ======= STEP: subscription wizard ======= */}
      {step === 'subscription' && (
        <SubscriptionWizard
          onComplete={handleSubscriptionComplete}
          onBack={() => setStep('review')}
          investmentAmount={amountNum}
          dealTitle={info?.title}
        />
      )}

      {/* ======= STEP: TAO flow (combined amount + address + submit) ======= */}
      {step === 'tao' && (
        <div className="p-5 space-y-5">
          {walletAddress && (
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-xs font-medium text-primary mb-1">Fund Wallet Address</p>
              <div className="flex items-center gap-2">
                <code className="text-xs text-foreground font-mono break-all flex-1 select-all">{walletAddress}</code>
                <Button variant="ghost" size="icon" onClick={copyAddress} className="flex-shrink-0 h-8 w-8">
                  {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Amount (TAO)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 25.0"
              value={taoAmount}
              onChange={e => setTaoAmount(e.target.value)}
              className="w-full px-3 py-3 rounded-lg border border-border bg-background text-foreground text-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Transfer via CLI</h3>
            <div className="p-3 rounded-lg bg-muted border border-border font-mono text-xs text-muted-foreground overflow-x-auto">
              <p className="text-foreground/60"># Bittensor CLI</p>
              <p>btcli wallet transfer \</p>
              <p className="pl-4">--dest {walletAddress} \</p>
              <p className="pl-4">--amount {taoAmount || '<amount>'}</p>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreementAccepted}
              onChange={e => setAgreementAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border accent-primary"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              I have sent TAO to the fund wallet and agree to the investment terms.
              I understand our team will verify the on-chain transaction.
            </span>
          </label>

          <Button
            onClick={handleTaoSubmit}
            disabled={!taoAmount || parseFloat(taoAmount) <= 0 || !agreementAccepted || submitting}
            className="w-full h-12 text-base font-semibold"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            I've Sent {taoAmount || '—'} TAO
          </Button>
        </div>
      )}

      {/* ======= STEP: docEsign ======= */}
      {step === 'docEsign' && (
        <div className="p-5">
          <DocEsignStep
            esignDocs={esignDocs}
            signedDocIds={signedDocIds}
            onDocSigned={(docId) => setSignedDocIds(prev => prev.includes(docId) ? prev : [...prev, docId])}
            onContinue={() => setStep('signature')}
            canContinue={esignDocs.every(d => signedDocIds.includes(d.id))}
          />
        </div>
      )}

      {/* ======= STEP: signature ======= */}
      {step === 'signature' && (
        <div className="p-5 space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-foreground">Sign your agreement</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Draw your electronic signature to complete your {formatCurrency(amountNum)} investment
            </p>
          </div>
          <div className="rounded-xl border-2 border-border bg-card p-5 text-sm text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Subscription Agreement</p>
            <p>By signing, you acknowledge you have read and agree to all terms of the offering documents. Your electronic signature is legally binding under the ESIGN Act and UETA.</p>
          </div>

          {/* C6: ESIGN Act mandatory consent disclosures (15 U.S.C. § 7001(c)) */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4 text-sm space-y-2">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Electronic Signature Consent Disclosure</p>
            <p className="text-amber-800 dark:text-amber-300 leading-relaxed">
              By proceeding, you <strong>consent to conduct this transaction electronically</strong> and
              to receive all related documents in electronic form.
            </p>
            <ul className="list-disc list-inside space-y-1 text-amber-800 dark:text-amber-300">
              <li><strong>Right to paper copies:</strong> Contact <a href="mailto:legal@honeyshares.com" className="underline">legal@honeyshares.com</a> to request paper copies (fees may apply).</li>
              <li><strong>Right to withdraw consent:</strong> You may withdraw before signing; withdrawal will not affect already-executed documents.</li>
            </ul>
          </div>

          <div className="rounded-xl border-2 border-border bg-card p-5">
            <SignaturePad
              label="Draw your signature"
              onSignatureCapture={setSignatureImage}
              existingSignature={signatureImage || undefined}
              width={500}
              height={180}
            />
          </div>
          <Button
            className="w-full h-12 text-base font-semibold disabled:opacity-40"
            disabled={!signatureImage || submitting}
            onClick={handleSubmit}
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            Sign &amp; Submit Investment
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            A signed copy will be emailed to you upon successful payment.
          </p>
        </div>
      )}

      {/* ======= STEP: processing ======= */}
      {step === 'processing' && (
        <div className="p-10 flex flex-col items-center justify-center text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
          <p className="text-lg font-medium text-foreground">Processing your investment…</p>
          <p className="text-sm text-muted-foreground">This may take a few seconds. Please don't close this window.</p>
        </div>
      )}

      {/* ======= STEP: receipt ======= */}
      {step === 'receipt' && result && (
        <div className="p-5 space-y-5">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Investment Confirmed</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Your investment in <span className="font-medium text-foreground">{result.dealTitle}</span> has been recorded.
            </p>
          </div>

          <Card className="p-4 border-border space-y-2 text-sm">
            <Row label="Transaction ID" value={result.txnId} mono />
            <Row label="Confirmation" value={result.confirmationCode} mono />
            <Row label="Amount" value={method === 'tao' ? `${taoAmount} TAO` : formatCurrency(result.amount)} bold />
            <Row label="Method" value={methodLabel(method)} />
            <Row label="Status" value={
              <span className="inline-flex items-center gap-1.5 text-yellow-600 font-medium">
                <Clock className="w-3.5 h-3.5" />
                {result.status === 'pending' ? 'Pending' : result.status}
              </span>
            } />
            <Row label="Date" value={new Date(result.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} />
          </Card>

          {method === 'ach' && (
            <div className="p-3 rounded-lg bg-muted border border-border text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">What happens next?</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>ACH debit initiated from your bank account</li>
                <li>Funds settle in 3-5 business days</li>
                <li>Your investment becomes <span className="text-green-600 font-medium">Active</span> once settled</li>
                <li>View your position anytime in the Investor Portal</li>
              </ol>
            </div>
          )}

          {method === 'tao' && (
            <div className="p-3 rounded-lg bg-muted border border-border text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">What happens next?</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Our team verifies your TAO transfer on-chain</li>
                <li>Your investment is activated once confirmed</li>
                <li>Emission payouts begin per the vesting schedule</li>
                <li>Track everything in the Investor Portal</li>
              </ol>
            </div>
          )}

          {method === 'wire' && (
            result.wireInstructions ? (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 space-y-3">
                <p className="text-sm font-medium text-amber-900">Complete Your Wire Transfer</p>
                <p className="text-xs text-amber-800">
                  Your investment is reserved. Send a wire for <span className="font-semibold">{formatCurrency(result.amount)}</span> using
                  the details below. Your position activates once we receive the funds (typically 1–2 business days).
                </p>
                <div className="rounded-md bg-white/70 border border-amber-200 divide-y divide-amber-100 text-xs">
                  {result.wireInstructions.bankName && (
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-muted-foreground">Bank</span>
                      <span className="font-medium text-foreground text-right">{result.wireInstructions.bankName}</span>
                    </div>
                  )}
                  {result.wireInstructions.accountName && (
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-muted-foreground">Account Name</span>
                      <span className="font-medium text-foreground text-right">{result.wireInstructions.accountName}</span>
                    </div>
                  )}
                  {result.wireInstructions.accountNumber && (
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-muted-foreground">Account Number</span>
                      <span className="font-mono font-medium text-foreground">{result.wireInstructions.accountNumber}</span>
                    </div>
                  )}
                  {result.wireInstructions.routingNumber && (
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-muted-foreground">Routing (ABA)</span>
                      <span className="font-mono font-medium text-foreground">{result.wireInstructions.routingNumber}</span>
                    </div>
                  )}
                  {result.wireInstructions.swiftCode && (
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-muted-foreground">SWIFT</span>
                      <span className="font-mono font-medium text-foreground">{result.wireInstructions.swiftCode}</span>
                    </div>
                  )}
                  {result.wireInstructions.reference && (
                    <div className="flex items-center justify-between px-3 py-2 bg-amber-100/60">
                      <span className="text-amber-900 font-medium">Reference / Memo</span>
                      <span className="font-mono font-semibold text-amber-900">{result.wireInstructions.reference}</span>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-amber-800/80">Include the reference above so we can match your wire to your account.</p>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-muted border border-border text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Wire Instructions</p>
                <p>Our team will email you wiring details shortly. Reference your confirmation code <span className="font-mono font-medium">{result.confirmationCode}</span> in the wire memo.</p>
              </div>
            )
          )}

          {method === 'crypto' && result.coinbase?.hostedUrl && (
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
              <p className="text-sm font-medium text-amber-900">Complete Your Crypto Payment</p>
              <p className="text-xs text-amber-800">
                Your investment is reserved. Click below to complete payment via Coinbase Commerce.
                Your investment will be activated once the transaction is confirmed on-chain.
              </p>
              <a
                href={result.coinbase.hostedUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full h-10 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Pay with Coinbase Commerce
              </a>
            </div>
          )}

          {/* Investment Documents */}
          {receiptDocs.length > 0 && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-2">
              <p className="text-xs font-medium text-blue-900 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> Investment Documents
              </p>
              <p className="text-xs text-blue-700">
                Please download and retain these documents for your records, bookkeeping, and financial reporting.
              </p>
              <div className="space-y-1.5">
                {receiptDocs.map(doc => (
                  <a
                    key={doc.id}
                    href={doc.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-blue-800 hover:text-blue-900 hover:bg-blue-100 rounded-md px-2 py-1.5 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate flex-1">{doc.title}</span>
                    <span className="text-blue-500 text-[10px] uppercase">{doc.document_type}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/portal/transactions'}
              className="flex-1 h-11"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Transactions
            </Button>
            <Button
              onClick={() => window.location.href = '/portal'}
              className="flex-1 h-11"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Investor Portal
            </Button>
          </div>

          <Button variant="ghost" onClick={onClose} className="w-full text-muted-foreground">
            Close
          </Button>
        </div>
      )}
    </Overlay>
  );
}

// ------------------------------------------------------------------
// Sub-components
// ------------------------------------------------------------------

function Overlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function MethodCard({ icon, title, desc, onClick, recommended }: { icon: React.ReactNode; title: string; desc: string; onClick: () => void; recommended?: boolean }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 rounded-lg border border-border hover:border-primary/60 hover:bg-primary/5 transition-all text-left group"
    >
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {recommended && (
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
              Recommended
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
    </button>
  );
}

function Row({ label, value, bold, mono }: { label: string; value: React.ReactNode; bold?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className={`text-sm text-foreground ${bold ? 'font-semibold' : ''} ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}

function StepIndicator({ current, method, hasEsign }: { current: FlowStep; method: string | null; hasEsign: boolean }) {
  const base = method === 'ach'
    ? ['method', 'bank', 'amount', 'review']
    : method === 'tao'
      ? ['method', 'tao']
      : ['method', 'amount', 'review'];
  const steps = method !== 'tao'
    ? [...base, 'subscription', ...(hasEsign ? ['docEsign'] : []), 'signature']
    : base;

  const idx = steps.indexOf(current);
  return (
    <div className="flex items-center justify-center gap-1.5 py-3">
      {steps.map((s, i) => (
        <div
          key={s}
          className={`h-1.5 rounded-full transition-all ${
            i <= idx ? 'bg-primary w-6' : 'bg-border w-4'
          }`}
        />
      ))}
    </div>
  );
}

function stepTitle(step: FlowStep, method: string | null): string {
  switch (step) {
    case 'method': return 'Invest';
    case 'bank': return 'Select Bank Account';
    case 'amount': return 'Investment Amount';
    case 'review': return 'Review & Confirm';
    case 'subscription': return 'Subscription Agreement';
    case 'docEsign': return 'Sign Documents';
    case 'signature': return 'Electronic Signature';
    case 'tao': return 'Invest with TAO';
    case 'processing': return 'Processing';
    case 'receipt': return 'Investment Receipt';
    default: return 'Invest';
  }
}

function methodLabel(method: string | null): string {
  switch (method) {
    case 'ach': return 'Bank Transfer (ACH)';
    case 'wire': return 'Wire Transfer';
    case 'tao': return 'TAO (Bittensor)';
    case 'crypto': return 'Crypto';
    default: return '—';
  }
}
